import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import EmailMetrics from './dashboard/EmailMetrics';
import { getEmailStatistics } from '../services/emailService';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [emailStats, setEmailStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const handleLogout = () => {
    logout();
    // Redirect happens automatically due to protected route
  };
  
  useEffect(() => {
    const fetchEmailStats = async () => {
      try {
        const stats = await getEmailStatistics();
        setEmailStats(stats);
      } catch (err) {
        console.error('Error fetching email statistics:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmailStats();
  }, []);

  return (
    <div className="dashboard">
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
        <div className="container">
          <Link className="navbar-brand" to="/dashboard">Professor Connect</Link>
          
          <button 
            className="navbar-toggler" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#navbarNav" 
            aria-controls="navbarNav" 
            aria-expanded="false" 
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <Link className="nav-link" to="/dashboard">Dashboard</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/professors">Professors</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/professor-metrics">Professor Metrics</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/outreach">My Outreach</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/templates">Email Templates</Link>
              </li>
            </ul>
            
            <div className="d-flex align-items-center">
              <Link to="/settings" className="btn btn-outline-light me-2">Settings</Link>
              
              <div className="dropdown">
                <button 
                  className="btn btn-outline-light dropdown-toggle" 
                  type="button" 
                  id="userDropdown" 
                  data-bs-toggle="dropdown" 
                  aria-expanded="false"
                >
                  {user?.firstName} {user?.lastName}
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                  <li><Link className="dropdown-item" to="/profile">Profile</Link></li>
                  <li><Link className="dropdown-item" to="/settings">Settings</Link></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><button className="dropdown-item" onClick={handleLogout}>Logout</button></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="dashboard-content">
        <div className="container py-5">
          <div className="row mb-4">
            <div className="col">
              <h1 className="welcome-message">Welcome, {user?.firstName}!</h1>
              <p className="text-muted">Here's an overview of your research outreach activities</p>
            </div>
          </div>

          <div className="row mb-5">
            <div className="col-md-4 mb-4">
              <div className="stat-card">
                <div className="stat-card-body">
                  <h5 className="stat-card-title">Professors Contacted</h5>
                  <p className="stat-card-value">
                    {loading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      emailStats?.total_emails || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-4">
              <div className="stat-card">
                <div className="stat-card-body">
                  <h5 className="stat-card-title">Responses Received</h5>
                  <p className="stat-card-value">
                    {loading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      emailStats?.responded_emails || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-4">
              <div className="stat-card">
                <div className="stat-card-body">
                  <h5 className="stat-card-title">Response Rate</h5>
                  <p className="stat-card-value">
                    {loading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      `${((emailStats?.response_rate || 0) * 100).toFixed(1)}%`
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col">
              <h2>Recent Activity</h2>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col">
              <div className="card">
                <div className="card-body">
                  {emailStats && emailStats.total_emails > 0 ? (
                    <EmailMetrics />
                  ) : (
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        <i className="bi bi-inbox"></i>
                      </div>
                      <h3>No recent activity</h3>
                      <p>Start by searching for professors and sending outreach emails</p>
                      <Link to="/professors" className="btn btn-primary">Browse Professors</Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-4 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Recommended Professors</h5>
                </div>
                <div className="card-body">
                  <div className="empty-state">
                    <p>No recommendations yet</p>
                    <Link to="/professors" className="btn btn-sm btn-outline-primary">Browse All Professors</Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Professor Metrics</h5>
                </div>
                <div className="card-body">
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <i className="bi bi-graph-up" style={{ fontSize: '2rem', marginBottom: '1rem', color: '#007bff' }}></i>
                    <p>View detailed metrics on professor responses</p>
                    <Link to="/professor-metrics" className="btn btn-sm btn-primary">View Metrics</Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">Your Email Templates</h5>
                </div>
                <div className="card-body">
                  <div className="empty-state">
                    <p>No custom templates yet</p>
                    <Link to="/templates" className="btn btn-sm btn-outline-primary">Create Template</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
